'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  useLogin,
  useLoginWithEmail,
  useLoginWithOAuth,
  useLoginWithSms,
} from '@privy-io/react-auth';

import { getPersistedReferralCode, useAuth, useBodyScrollLock, useT } from '@/hooks';
import { useMixpanel } from '@/hooks/useMixpanel';
import { setOAuthContext } from '@/lib/oauthContext';
import { useAppDispatch } from '@/store';
import { closeModal } from '@/store/slices/ui.slice';
import { colors } from '@/styles/theme';
import { AuthModalErrorAlert } from './AuthModalErrorAlert';
import { PrivyOtpVerification } from './PrivyOtpVerification';

import { MESSAGES } from '@/constants/messages.constants';

// Google "G" in a single white glyph — for the filled primary button.
const GoogleIconMono = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#ffffff" aria-hidden="true">
    <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 1 1 0-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0 0 12.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
  </svg>
);

// Apple logo SVG — always white so it works on the black icon box
const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="rgba(255,255,255,0.92)">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.08-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

const DIAL_CODES = [
  { label: 'MX', dial: '+52' },
  { label: 'US', dial: '+1' },
  { label: 'CA', dial: '+1' },
  { label: 'ES', dial: '+34' },
  { label: 'AR', dial: '+54' },
  { label: 'CO', dial: '+57' },
  { label: 'CL', dial: '+56' },
  { label: 'PE', dial: '+51' },
  { label: 'BR', dial: '+55' },
  { label: 'VE', dial: '+58' },
  { label: 'GT', dial: '+502' },
  { label: 'EC', dial: '+593' },
  { label: 'GB', dial: '+44' },
  { label: 'FR', dial: '+33' },
  { label: 'DE', dial: '+49' },
  { label: 'IT', dial: '+39' },
  { label: 'IN', dial: '+91' },
  { label: 'AU', dial: '+61' },
  { label: 'JP', dial: '+81' },
];

type AuthStep = 'main' | 'phone' | 'email_otp' | 'phone_otp';

const ICON_BOX_SX = {
  width: 28,
  height: 28,
  borderRadius: '8px',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
  color: 'text.secondary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
} as const;

const OPTION_BTN_SX = {
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  gap: 1.5,
  px: 1.5,
  py: 1.25,
  mb: 1,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  color: 'text.primary',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 500,
  fontSize: '0.9375rem',
  textAlign: 'left' as const,
  transition: 'background-color 0.15s',
  '&:hover': {
    bgcolor: (theme: any) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
  },
  '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
} as const;

// Primary filled Google CTA (top of the modal).
const GOOGLE_BTN_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  width: '100%',
  py: 1.5,
  borderRadius: '12px',
  border: 'none',
  bgcolor: 'primary.main',
  color: 'primary.contrastText',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 700,
  fontSize: '0.9375rem',
  boxShadow: (theme: any) => `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
  transition: 'background-color 0.15s, box-shadow 0.15s',
  '&:hover': { bgcolor: 'primary.dark' },
} as const;

// Email row: bordered pill with an inline "Continue" button.
const EMAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  height: 48,
  boxSizing: 'border-box',
  pl: 1.75,
  pr: 0.75,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  gap: 1,
  transition: 'all 0.2s ease-in-out',
  '&:focus-within': {
    borderColor: 'primary.main',
    boxShadow: (theme: any) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
  },
} as const;

const EMAIL_INPUT_SX = {
  flex: 1,
  minWidth: 0,
  height: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: 'text.primary',
  fontFamily: 'inherit',
  fontSize: '0.9375rem',
  p: 0,
  '&::placeholder': { color: 'text.disabled', opacity: 1 },
} as const;

// A square method tile in the icon grid (Apple / WhatsApp / Wallet).
const METHOD_TILE_SX = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.75,
  py: 1.5,
  borderRadius: '12px',
  border: '1px solid',
  borderColor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.08)',
  bgcolor: (theme: any) =>
    theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
  color: 'text.primary',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'background-color 0.15s, border-color 0.15s',
  '&:hover': {
    bgcolor: (theme: any) =>
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    borderColor: 'primary.main',
  },
} as const;

const TILE_LABEL_SX = { fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' } as const;

const FOOTER_LINK_SX = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: 'text.secondary',
  textDecoration: 'none',
  '&:hover': { color: 'text.primary', textDecoration: 'underline' },
} as const;

export interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** When true (Ambassador invite flow), the modal cannot be dismissed until login succeeds. */
  locked?: boolean;
}

export function AuthModal({ isOpen, onClose, locked = false }: AuthModalProps) {
  const { t } = useT();
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const { isAuthenticated, user } = useAuth();
  const {
    trackLoginSucceeded,
    trackLoginFailed,
    trackLoginStarted,
    trackSignupCompleted,
    trackReferralApplied,
    trackReferralScreenViewed,
    registerReferralCode,
  } = useMixpanel();
  useBodyScrollLock(isOpen);

  const [step, setStep] = useState<AuthStep>('main');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  // Which method is currently sending a code, so the spinner only shows on that
  // button. A single shared boolean lit up both the email and phone submit
  // buttons at once. `isSendingCode` stays as a derived "any send in progress"
  // flag for disabling inputs while a request is in flight.
  const [sendingMethod, setSendingMethod] = useState<'email' | 'sms' | null>(null);
  const isSendingCode = sendingMethod !== null;
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialCode, setDialCode] = useState(DIAL_CODES[0].dial);
  const [countryLabel, setCountryLabel] = useState(DIAL_CODES[0].label);
  const [countryMenuAnchor, setCountryMenuAnchor] = useState<null | HTMLElement>(null);

  const handleClose = useCallback(() => {
    setStep('main');
    setEmail('');
    setPhone('');
    setOtpCode('');
    setError(null);
    setCountryMenuAnchor(null);
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen && isAuthenticated && user) {
      handleClose();
    }
  }, [isOpen, isAuthenticated, user, handleClose]);

  // Referral funnel: when the auth prompt opens for a not-yet-authenticated user
  // who arrived via a referral link, record the "referral screen" step once.
  const referralScreenTrackedRef = useRef(false);
  useEffect(() => {
    if (!isOpen || isAuthenticated) {
      referralScreenTrackedRef.current = false;
      return;
    }
    if (referralScreenTrackedRef.current) return;
    const referralCode = getPersistedReferralCode();
    if (referralCode) {
      referralScreenTrackedRef.current = true;
      registerReferralCode(referralCode);
      trackReferralScreenViewed({ referral_code: referralCode });
    }
  }, [isOpen, isAuthenticated, registerReferralCode, trackReferralScreenViewed]);

  const onLoginComplete = useCallback(
    (params?: { isNewUser?: boolean; loginMethod?: string | null }) => {
      const loginMethod = params?.loginMethod || 'privy';
      const isNewUser = Boolean(params?.isNewUser);

      // Compute a real days_since_last_login from the last recorded login instead
      // of the previous hardcoded 0 (which made the "returning users" metric unusable).
      let daysSinceLastLogin: number | undefined;
      try {
        const last = localStorage.getItem('pm_last_login_at');
        if (last) {
          daysSinceLastLogin = Math.floor((Date.now() - Number(last)) / 86_400_000);
        }
        localStorage.setItem('pm_last_login_at', String(Date.now()));
      } catch {
        // localStorage unavailable — non-fatal, just omit the metric.
      }

      trackLoginSucceeded({
        login_method: loginMethod,
        days_since_last_login: daysSinceLastLogin,
        is_new_user: isNewUser,
      });

      // First-ever login == signup. Emit signup_completed (+ referral_applied if
      // the user arrived via a referral link) so the admin funnel and referral
      // analytics have real events instead of proxies.
      // NOTE: this only READS the persisted referral code for tracking — it must
      // NOT clear it. AuthInitializer is the single owner of the referral flow:
      // it sends the code to the backend login and clears it after the referral
      // is applied. Clearing here would race ahead of that call, drop the code,
      // and leave the user un-referred (which pops the InviteGate modal).
      if (isNewUser) {
        const referralCode = getPersistedReferralCode();
        if (referralCode) {
          registerReferralCode(referralCode);
          trackReferralApplied({ referral_code: referralCode, signup_method: loginMethod });
        }
        trackSignupCompleted({
          signup_method: loginMethod,
          referral_code: referralCode || undefined,
        });
      }

      // Embedded wallet creation is owned by AuthInitializer (always mounted via
      // providers.tsx): it reacts to `authenticated` flipping true and retries if
      // the wallet iframe isn't ready yet. Creating it here too raced that logic
      // and could throw "iframe not initialized" right after a headless login.
      dispatch(closeModal('signIn'));
    },
    [
      trackLoginSucceeded,
      trackSignupCompleted,
      trackReferralApplied,
      registerReferralCode,
      dispatch,
    ],
  );

  const onLoginError = useCallback(
    (err: any) => {
      const errStr = typeof err === 'string' ? err : err?.message || err?.type || '';
      if (!errStr.toLowerCase().includes('user_exited')) {
        trackLoginFailed({ login_method: 'privy', failure_reason: errStr });
        setError(errStr || t('messages.auth.loginFailed'));
      }
      setIsVerifying(false);
      setSendingMethod(null);
    },
    [trackLoginFailed, t],
  );

  const { sendCode: sendEmailCode, loginWithCode: loginWithEmailCode } = useLoginWithEmail({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { initOAuth } = useLoginWithOAuth({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { sendCode: sendSmsCode, loginWithCode: loginWithSmsCode } = useLoginWithSms({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  const { login: loginWithWallet } = useLogin({
    onComplete: onLoginComplete,
    onError: onLoginError,
  });

  // ── Email flow ─────────────────────────────────────────────
  const handleEmailSubmit = async () => {
    if (!email.trim()) return;
    setSendingMethod('email');
    setError(null);
    try {
      trackLoginStarted({ signup_method: 'email', source_page: 'auth_modal' });
      await sendEmailCode({ email: email.trim() });
      setStep('email_otp');
    } catch (err: any) {
      const msg = err?.message || MESSAGES.AUTH.OTP_SEND_FAILED;
      toast.error(msg);
      setError(msg);
    } finally {
      setSendingMethod(null);
    }
  };

  const handleEmailOtpVerify = async () => {
    setIsVerifying(true);
    setError(null);
    try {
      await loginWithEmailCode({ code: otpCode });
    } catch {
      setIsVerifying(false);
    }
  };

  const handleEmailResend = async () => {
    setOtpCode('');
    setError(null);
    try {
      await sendEmailCode({ email: email.trim() });
    } catch (err: any) {
      setError(err?.message || MESSAGES.AUTH.OTP_SEND_FAILED);
    }
  };

  // ── Social flows ───────────────────────────────────────────
  const handleGoogleSignIn = () => {
    setError(null);
    trackLoginStarted({ signup_method: 'google', source_page: 'auth_modal' });
    // Apple/Google can force a full top-level redirect instead of a popup on
    // mobile/in-app browsers, which reloads the page and drops all React state.
    // Persist where to return the user so AuthInitializer can send them back
    // once it picks the session back up after reload (see oauthContext.ts).
    setOAuthContext('google', pathname || '/');
    initOAuth({ provider: 'google' });
  };

  const handleAppleSignIn = () => {
    setError(null);
    trackLoginStarted({ signup_method: 'apple', source_page: 'auth_modal' });
    setOAuthContext('apple', pathname || '/');
    initOAuth({ provider: 'apple' });
  };

  // ── WhatsApp / SMS flow ────────────────────────────────────
  const handleWhatsAppClick = () => {
    setError(null);
    setStep('phone');
  };

  // ── Wallet flow ────────────────────────────────────────────
  // Open Privy's wallet picker directly (no intermediate step).
  const handleWallet = () => {
    setError(null);
    trackLoginStarted({ signup_method: 'wallet', source_page: 'auth_modal' });
    loginWithWallet({ loginMethods: ['wallet'] } as any);
  };

  const handlePhoneSubmit = async () => {
    if (!phone.trim()) return;
    const fullNumber = dialCode + phone.replace(/\s/g, '');
    setSendingMethod('sms');
    setError(null);
    try {
      trackLoginStarted({ signup_method: 'sms', source_page: 'auth_modal' });
      await sendSmsCode({ phoneNumber: fullNumber });
      setOtpCode('');
      setStep('phone_otp');
    } catch (err: any) {
      const msg = err?.message || MESSAGES.AUTH.OTP_SEND_FAILED;
      toast.error(msg);
      setError(msg);
    } finally {
      setSendingMethod(null);
    }
  };

  const handlePhoneOtpVerify = async () => {
    setIsVerifying(true);
    setError(null);
    try {
      await loginWithSmsCode({ code: otpCode });
    } catch {
      setIsVerifying(false);
    }
  };

  const handlePhoneResend = async () => {
    setOtpCode('');
    setError(null);
    try {
      await sendSmsCode({ phoneNumber: phone.trim() });
    } catch (err: any) {
      setError(err?.message || MESSAGES.AUTH.OTP_SEND_FAILED);
    }
  };

  const handleBack = () => {
    setError(null);
    setOtpCode('');
    // Stepping back from the OTP screen returns to phone entry; everything else
    // returns to the main method list.
    setStep(step === 'phone_otp' ? 'phone' : 'main');
  };

  // ── Shared top bar (close / back) ─────────────────────────
  const renderTopBar = (showBack = false) => (
    <Box sx={{ position: 'relative', height: 52, flexShrink: 0 }}>
      {showBack && (
        <IconButton
          onClick={handleBack}
          aria-label={t('authModal.back')}
          sx={{
            position: 'absolute',
            left: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            color: 'text.secondary',
            width: 32,
            height: 32,
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
              color: 'text.primary',
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
      {!locked && (
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
            color: 'text.secondary',
            width: 32,
            height: 32,
            border: (theme) =>
              theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.1)'
                : '1px solid rgba(0,0,0,0.08)',
            '&:hover': {
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)',
              color: 'text.primary',
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      )}
    </Box>
  );

  // ── Main step ──────────────────────────────────────────────
  // ── Brand header (Porvenix mark + "Log in or sign up") ─────
  const renderBrandHeader = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: { xs: 2, sm: 3 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          width: { xs: 44, sm: 48 },
          height: { xs: 44, sm: 48 },
          borderRadius: '50%',
          overflow: 'hidden',
          mb: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.primary',
        }}
      >
        <svg
          width="48"
          height="48"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="16"
            cy="16"
            r="14"
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="4"
            y1="16"
            x2="28"
            y2="16"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
          />
        </svg>
      </Box>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{
          color: 'text.primary',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
          textAlign: 'center',
        }}
      >
        {t('authModal.loginOrSignUp')}
      </Typography>
    </Box>
  );

  const renderMainStep = () => (
    <>
      {renderTopBar()}
      {renderBrandHeader()}

      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />

        {/* Google — primary CTA */}
        <Box component="button" type="button" onClick={handleGoogleSignIn} sx={GOOGLE_BTN_SX}>
          <GoogleIconMono />
          {t('authModal.continueWithGoogleFull')}
        </Box>

        {/* OR divider */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my: 2 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: 'text.disabled',
            }}
          >
            {t('authModal.or')}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
        </Box>

        {/* Email + inline Continue */}
        <Box sx={EMAIL_ROW_SX}>
          <Box
            component="input"
            type="email"
            value={email}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleEmailSubmit()}
            disabled={isSendingCode}
            placeholder={t('authModal.emailPlaceholder')}
            sx={EMAIL_INPUT_SX}
          />
          <Box
            component="button"
            type="button"
            onClick={handleEmailSubmit}
            disabled={isSendingCode || !email.trim()}
            sx={(theme) => ({
              flexShrink: 0,
              px: 1.75,
              height: 36,
              borderRadius: '8px',
              border: 'none',
              cursor: email.trim() && !isSendingCode ? 'pointer' : 'default',
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: '0.875rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
              bgcolor:
                email.trim() && !isSendingCode
                  ? alpha(theme.palette.primary.main, 0.15)
                  : theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.05)'
                    : 'rgba(0,0,0,0.04)',
              color:
                email.trim() && !isSendingCode
                  ? theme.palette.primary.main
                  : theme.palette.text.disabled,
            })}
          >
            {sendingMethod === 'email' ? (
              <CircularProgress size={14} sx={{ color: 'text.secondary' }} />
            ) : (
              t('authModal.continue')
            )}
          </Box>
        </Box>

        {/* Method grid: Apple · WhatsApp · Wallet. Each opens a focused step. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mt: 1.5 }}>
          {/* Apple */}
          <Box
            component="button"
            type="button"
            onClick={handleAppleSignIn}
            aria-label={t('authModal.continueWithApple')}
            sx={METHOD_TILE_SX}
          >
            <Box sx={{ ...ICON_BOX_SX, bgcolor: '#1d1d1f' }}>
              <AppleIcon />
            </Box>
            <Typography sx={TILE_LABEL_SX}>Apple</Typography>
          </Box>

          {/* WhatsApp */}
          <Box
            component="button"
            type="button"
            onClick={handleWhatsAppClick}
            aria-label={t('authModal.continueWithWhatsApp')}
            sx={METHOD_TILE_SX}
          >
            <Box sx={ICON_BOX_SX}>
              <WhatsAppIcon sx={{ fontSize: 20, color: colors.brand.whatsapp }} />
            </Box>
            <Typography sx={TILE_LABEL_SX}>WhatsApp</Typography>
          </Box>

          {/* Wallet */}
          <Box
            component="button"
            type="button"
            onClick={handleWallet}
            aria-label={t('authModal.continueWithWallet')}
            sx={METHOD_TILE_SX}
          >
            <Box sx={ICON_BOX_SX}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: 'inherit' }} />
            </Box>
            <Typography sx={TILE_LABEL_SX}>{t('authModal.wallet')}</Typography>
          </Box>
        </Box>
      </Box>

      {/* Terms · Privacy footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          py: 2,
        }}
      >
        <Link component={NextLink} href="/terms" onClick={handleClose} sx={FOOTER_LINK_SX}>
          {t('authModal.terms')}
        </Link>
        <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'text.disabled' }} />
        <Link component={NextLink} href="/privacy" onClick={handleClose} sx={FOOTER_LINK_SX}>
          {t('authModal.privacy')}
        </Link>
      </Box>
    </>
  );

  // ── Phone input step ───────────────────────────────────────
  // ── Focused step header (icon + label) ────────────────────
  const renderStepHeader = (icon: React.ReactNode, label: string) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        mb: { xs: 2, sm: 2.5 },
        px: { xs: 2, sm: 3 },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '14px',
          mb: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
        }}
      >
        {icon}
      </Box>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{
          color: 'text.primary',
          fontSize: { xs: '1.05rem', sm: '1.15rem' },
          textAlign: 'center',
        }}
      >
        {label}
      </Typography>
    </Box>
  );

  // ── Phone step (WhatsApp / SMS) — focused, with back ───────
  const renderPhoneStep = () => (
    <>
      {renderTopBar(true)}
      {renderStepHeader(
        <WhatsAppIcon sx={{ fontSize: 26, color: colors.brand.whatsapp }} />,
        t('authModal.continueWithWhatsApp'),
      )}
      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />
        <Typography sx={{ color: 'text.secondary', fontSize: '0.8125rem', mb: 1 }}>
          {t('authModal.phoneInputLabel')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 48,
            boxSizing: 'border-box',
            px: 1.5,
            borderRadius: '12px',
            border: '1px solid',
            borderColor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            gap: 1.25,
            transition: 'all 0.2s ease-in-out',
            '&:focus-within': {
              borderColor: 'primary.main',
              boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
            },
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={(e: React.MouseEvent<HTMLElement>) => setCountryMenuAnchor(e.currentTarget)}
            disabled={isSendingCode}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: 'text.primary',
              fontFamily: 'inherit',
              fontWeight: 600,
              fontSize: '0.875rem',
              p: 0,
              flexShrink: 0,
              whiteSpace: 'nowrap',
            }}
          >
            {countryLabel} {dialCode}
            <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
          </Box>
          <Box
            sx={{
              width: '1px',
              height: 18,
              bgcolor: (theme) =>
                theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
              flexShrink: 0,
            }}
          />
          <Box
            component="input"
            autoFocus
            type="tel"
            value={phone}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handlePhoneSubmit()}
            disabled={isSendingCode}
            placeholder="555 000 0000"
            sx={{
              flex: 1,
              height: '100%',
              border: 'none',
              outline: 'none',
              background: 'transparent',
              color: 'text.primary',
              fontFamily: 'inherit',
              fontSize: '0.9375rem',
              minWidth: 0,
              p: 0,
              '&::placeholder': { color: 'text.disabled', opacity: 1 },
            }}
          />
          <Box
            component="button"
            type="button"
            onClick={handlePhoneSubmit}
            disabled={isSendingCode || !phone.trim()}
            sx={{
              background: 'none',
              border: 'none',
              cursor: phone.trim() && !isSendingCode ? 'pointer' : 'default',
              color: phone.trim() && !isSendingCode ? 'primary.main' : 'text.disabled',
              fontWeight: 600,
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              p: 0,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {sendingMethod === 'sms' ? (
              <CircularProgress size={14} sx={{ color: 'primary.main' }} />
            ) : (
              t('authModal.submit')
            )}
          </Box>
        </Box>

        <Menu
          anchorEl={countryMenuAnchor}
          open={Boolean(countryMenuAnchor)}
          onClose={() => setCountryMenuAnchor(null)}
          slotProps={{ paper: { sx: { maxHeight: 240, minWidth: 140 } } }}
        >
          {DIAL_CODES.map((c) => (
            <MenuItem
              key={`${c.label}-${c.dial}`}
              selected={c.label === countryLabel && c.dial === dialCode}
              onClick={() => {
                setCountryLabel(c.label);
                setDialCode(c.dial);
                setCountryMenuAnchor(null);
              }}
              sx={{ fontSize: '0.875rem', fontWeight: 500 }}
            >
              {c.label}&nbsp;&nbsp;{c.dial}
            </MenuItem>
          ))}
        </Menu>
      </Box>
    </>
  );

  // ── OTP step (reused for email & phone) ───────────────────
  const renderOtpStep = (displayContact: string, onVerify: () => void, onResend: () => void) => (
    <>
      {renderTopBar(true)}
      <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 3 }}>
        <AuthModalErrorAlert error={error} onClose={() => setError(null)} />
        <PrivyOtpVerification
          email={displayContact}
          otpCode={otpCode}
          setOtpCode={setOtpCode}
          isAuthenticating={isVerifying}
          onVerify={onVerify}
          onResend={onResend}
        />
      </Box>
    </>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={locked ? undefined : handleClose}
      disableEscapeKeyDown={locked}
      maxWidth="xs"
      fullWidth
      aria-labelledby="auth-modal-title"
      PaperProps={{
        elevation: 0,
        sx: (theme) => ({
          borderRadius: { xs: '16px', sm: '20px' },
          bgcolor: theme.palette.background.paper,
          background: theme.palette.background.paper,
          backgroundImage: 'none !important',
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          maxWidth: { xs: '100%', sm: '444px' },
          mx: 'auto',
          maxHeight: { xs: '88vh', sm: '90vh' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 8px 40px ${alpha(theme.palette.common.black, 0.6)}, 0 0 40px ${alpha(theme.palette.primary.main, 0.06)}`
              : `0 8px 40px ${alpha(theme.palette.common.black, 0.15)}`,
        }),
      }}
      BackdropProps={{
        sx: (theme) => ({
          backgroundColor:
            theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.65)' : 'rgba(15, 23, 42, 0.4)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }),
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {step === 'main' && renderMainStep()}
        {step === 'phone' && renderPhoneStep()}
        {step === 'email_otp' && renderOtpStep(email, handleEmailOtpVerify, handleEmailResend)}
        {step === 'phone_otp' && renderOtpStep(phone, handlePhoneOtpVerify, handlePhoneResend)}
      </DialogContent>
    </Dialog>
  );
}

export default AuthModal;
